import { InstallPrompt as InstallPromptComponent } from "gsintegrations";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  setPwaInstallPromptAvailability,
  subscribeToPwaInstallRequests,
} from "../utils/pwaInstallRequest";
import { toast } from "../utils/toast";
import "./InstallPrompt.css";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let cachedDeferredPrompt: BeforeInstallPromptEvent | null = null;

const INITIAL_SHOW_DELAY_MS = 500;
const REOPEN_DELAY_MS = 1500;
const MANUAL_FALLBACK_DELAY_MS = 1200;
type InstallPromptMode = "native" | "manual";

const isStandaloneMode = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const mediaQueryStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;
  const androidStandalone =
    typeof document !== "undefined" &&
    document.referrer.includes("android-app://");

  return mediaQueryStandalone || iosStandalone || androidStandalone;
};

const isMobileOrTablet = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.matchMedia?.("(pointer: coarse)")?.matches) {
    return true;
  }

  const ua = window.navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|mobile|tablet/.test(ua);
};

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(cachedDeferredPrompt);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installPromptMode, setInstallPromptMode] = useState<InstallPromptMode>(
    cachedDeferredPrompt ? "native" : "manual",
  );
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneMode());

  const reopenTimerRef = useRef<number | null>(null);
  const manualFallbackTimerRef = useRef<number | null>(null);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(
    cachedDeferredPrompt,
  );
  const isInstalledRef = useRef(isStandalone);

  const clearReopenTimer = useCallback(() => {
    if (reopenTimerRef.current !== null) {
      window.clearTimeout(reopenTimerRef.current);
      reopenTimerRef.current = null;
    }
  }, []);

  const clearManualFallbackTimer = useCallback(() => {
    if (manualFallbackTimerRef.current !== null) {
      window.clearTimeout(manualFallbackTimerRef.current);
      manualFallbackTimerRef.current = null;
    }
  }, []);

  const markInstalled = useCallback(() => {
    isInstalledRef.current = true;
    setPwaInstallPromptAvailability(false);
    cachedDeferredPrompt = null;
    clearReopenTimer();
    clearManualFallbackTimer();
    setIsStandalone(true);
    setShowInstallPrompt(false);
    setInstallPromptMode("manual");
    deferredPromptRef.current = null;
    setDeferredPrompt(null);
  }, [clearManualFallbackTimer, clearReopenTimer]);

  const schedulePromptOpen = useCallback(
    (delayMs: number) => {
      clearReopenTimer();

      if (isInstalledRef.current) {
        return;
      }

      reopenTimerRef.current = window.setTimeout(() => {
        reopenTimerRef.current = null;

        if (!isInstalledRef.current) {
          setShowInstallPrompt(true);
        }
      }, delayMs);
    },
    [clearReopenTimer],
  );

  useEffect(() => {
    isInstalledRef.current = isStandalone;
  }, [isStandalone]);

  useEffect(() => {
    deferredPromptRef.current = deferredPrompt;
  }, [deferredPrompt]);

  const handleInstall = useCallback(async () => {
    const promptEvent = deferredPromptRef.current;

    if (!promptEvent || isInstalledRef.current) {
      return;
    }

    setShowInstallPrompt(false);

    try {
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;

      if (outcome === "accepted") {
        markInstalled();
        return;
      }

      schedulePromptOpen(REOPEN_DELAY_MS);
    } catch {
      schedulePromptOpen(REOPEN_DELAY_MS);
    }
  }, [markInstalled, schedulePromptOpen]);

  const showManualFallback = useCallback(() => {
    if (isInstalledRef.current || deferredPromptRef.current) {
      return;
    }

    setInstallPromptMode("manual");
    setShowInstallPrompt(true);
  }, []);

  const triggerInstallRequest = useCallback(() => {
    if (isInstalledRef.current) {
      return;
    }

    if (deferredPromptRef.current) {
      setInstallPromptMode("native");
      setShowInstallPrompt(true);
      return;
    }

    clearManualFallbackTimer();
    manualFallbackTimerRef.current = window.setTimeout(() => {
      manualFallbackTimerRef.current = null;
      showManualFallback();
    }, MANUAL_FALLBACK_DELAY_MS);
  }, [clearManualFallbackTimer, showManualFallback]);

  useEffect(() => {
    if (isStandaloneMode()) {
      markInstalled();
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();

      if (isInstalledRef.current) {
        return;
      }

      clearManualFallbackTimer();
      const promptEvent = event as BeforeInstallPromptEvent;
      deferredPromptRef.current = promptEvent;
      cachedDeferredPrompt = promptEvent;
      setPwaInstallPromptAvailability(true);
      setDeferredPrompt(promptEvent);
      setInstallPromptMode("native");
      schedulePromptOpen(INITIAL_SHOW_DELAY_MS);
    };

    const handleAppInstalled = () => {
      markInstalled();
    };

    const displayMode = window.matchMedia?.("(display-mode: standalone)");
    const handleDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        markInstalled();
      }
    };

    if (displayMode?.addEventListener) {
      displayMode.addEventListener("change", handleDisplayModeChange);
    } else if (displayMode?.addListener) {
      displayMode.addListener(handleDisplayModeChange);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      clearReopenTimer();
      clearManualFallbackTimer();

      if (displayMode?.removeEventListener) {
        displayMode.removeEventListener("change", handleDisplayModeChange);
      } else if (displayMode?.removeListener) {
        displayMode.removeListener(handleDisplayModeChange);
      }

      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [
    clearManualFallbackTimer,
    clearReopenTimer,
    markInstalled,
    schedulePromptOpen,
  ]);

  const handleDismiss = useCallback(() => {
    if (isInstalledRef.current) {
      return;
    }

    setShowInstallPrompt(false);
    schedulePromptOpen(REOPEN_DELAY_MS);
  }, [schedulePromptOpen]);

  useEffect(() => {
    return subscribeToPwaInstallRequests(triggerInstallRequest);
  }, [triggerInstallRequest]);

  const handleManualInstall = useCallback(() => {
    const promptEvent = deferredPromptRef.current;

    if (promptEvent) {
      setInstallPromptMode("native");
      void handleInstall();
      return;
    }

    toast.info("Instalacao manual no navegador", {
      description:
        "No Chrome: clique no icone de instalar na barra de endereco, ao lado da estrela.",
    });
  }, [handleInstall]);

  if (isStandalone || !showInstallPrompt) {
    return null;
  }

  const isNativePromptMode = installPromptMode === "native" && !!deferredPrompt;
  const manualInstallText = isMobileOrTablet()
    ? "Para instalar agora: clique no ícone dos três pontos do navegador e depois em 'Adicionar à tela inicial'"
    : "Para instalar agora: clique no ícone de instalar na barra de navegação, ao lado esquerdo da estrela";

  const args = {
    appName: "I ❤️ Macumba",
    iconSrc: "/logo.webp",
    benefitsText: isNativePromptMode
      ? "A plataforma para terreiros e médiuns: agende rituais, gerencie presenças e fortaleça sua comunidade."
      : manualInstallText,
    featureLine: isNativePromptMode
      ? "Instala localmente • Sem conta de loja • Desinstale a qualquer momento"
      : "Siga os passos do navegador para instalar o aplicativo neste aparelho.",
    // Keep schema-valid value; wrapper-level reopen policy enforces visibility invariant.
    dismissCooldownDays: 1,
    disableAutoOpen: true,
    forceOpen: true,
    onInstall: isNativePromptMode ? handleInstall : handleManualInstall,
    onDismiss: handleDismiss,
  };

  return <InstallPromptComponent {...args} />;
};

export default InstallPrompt;
